import { AnalyticsService } from 'base/analytics';
import { LibraryCache } from 'base/library_cache';
import { extractPaletteColor } from 'base/styleguide/color';
import { getShadeKey } from 'base/styleguide/concept';
import { Button } from 'base/ui/button';
import { IconClearSmall, IconPng } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { LoadingDots } from 'base/ui/loading_dots';
import { PanelLayout } from 'base/ui/panel';
import { ToastQueue } from 'base/ui/toast';
import { TextSmall } from 'base/ui/typography';
import { useStableCallback } from 'base/useStableCallback';
import { ProjectSession } from 'blocks/editor/session/session';
import { UndoManager } from 'blocks/woot_woot/undo';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { getUserJwt } from '../../../../library/src/modules/cookies';
import { Section } from '../model';
import { ConceptAPI, getGlobalConceptsStore } from './concept_store';
import styles from './imagery_panel.css';
import { findOverridableImagesInElement, OverridableImage } from './images';
import {
  globalConceptSectionHoverRegistry,
  globalImageHoverRegistry,
  globalImageLoadingRegistry,
} from './image_styles';
import {
  compiledSchemeVariables,
  computeImagePlaceholderColor,
} from './style_application/generate_schemes';

export const ImageryPanel = observer(
  ({
    session,
    navigation,
    library,
    undo,
    toastQueue,
    analytics,
    concept,
    section,
  }: {
    session: ProjectSession;
    navigation: { close: () => void };
    library: LibraryCache;
    undo: UndoManager;
    toastQueue: ToastQueue;
    analytics: AnalyticsService;
    concept: ConceptAPI;
    section: Section | null;
  }) => {
    const rootElementId = section?.element.current?.id;
    const rootElementSlug = section?.element.current?.source.slug;

    const scheme =
      (rootElementId
        ? concept.schemes.find(
            (scheme) =>
              getShadeKey(scheme) ===
              concept.state.schemeOverrideByElementId.get(rootElementId),
          )
        : undefined) ?? concept.schemes[0];

    const vars = compiledSchemeVariables(scheme);

    const background = extractPaletteColor(
      concept,
      vars['color-scheme-1-background'],
    );

    const { placeholderForeground, placeholderBackground, placeholderImage } =
      computeImagePlaceholderColor(vars['color-scheme-1-background'], (color) =>
        extractPaletteColor(concept, color),
      );

    const [elementImages, setElementImages] = useState<
      OverridableImage[] | null
    >(null);

    useEffect(() => {
      if (!section?.element.current) return;
      findOverridableImagesInElement(
        section.element.current,
        library,
        undo,
      ).then((result) => setElementImages(result));
    }, [library, section?.element, undo]);

    if (!rootElementId) return null;

    return (
      <PanelLayout title="Section images" navigation={navigation}>
        {elementImages && (
          <Rows spacing={12}>
            {elementImages.map((image, i) => (
              <ImageCard
                session={session}
                rootElementId={rootElementId}
                rootElementSlug={rootElementSlug}
                concept={concept}
                image={image}
                key={i}
                index={i + 1}
                toastQueue={toastQueue}
                bgColor={background}
                placeholderFgColor={placeholderForeground}
                placeholderBgColor={placeholderBackground}
                placeholderImage={placeholderImage}
                analytics={analytics}
              />
            ))}
          </Rows>
        )}
      </PanelLayout>
    );
  },
);

const ImageCard = observer(
  ({
    session,
    rootElementId,
    rootElementSlug,
    image,
    concept,
    index,
    toastQueue,
    bgColor,
    placeholderFgColor,
    placeholderBgColor,
    placeholderImage,
    analytics,
  }: {
    session: ProjectSession;
    rootElementId: string;
    rootElementSlug: string | undefined;
    image: OverridableImage;
    concept: ConceptAPI;
    index: number;
    toastQueue: ToastQueue;
    bgColor: string | undefined;
    placeholderFgColor: string | undefined;
    placeholderBgColor: string | undefined;
    placeholderImage: string | undefined;
    analytics: AnalyticsService;
  }) => {
    const { onUpload } = useImageUpload({
      concept,
      imageKey: image.key,
      toastQueue,
    });

    const handleUpload = useStableCallback(async (files: File[]) => {
      const isLoggedIn = !!getUserJwt();
      if (!isLoggedIn) {
        return session.showAuthWall?.();
      }

      if (files.length === 0) return;

      analytics.track({
        event: 'project:image_upload',
        section_slug: rootElementSlug,
        section_id: rootElementId,
        index,
      });

      globalImageLoadingRegistry.add(image.key);
      try {
        await onUpload(files);
      } finally {
        globalImageLoadingRegistry.delete(image.key);
      }
    });

    const handleRemove = useStableCallback(() => {
      analytics.track({
        event: 'project:image_remove',
        section_slug: rootElementSlug,
        section_id: rootElementId,
        index,
      });

      concept.clearImage(image.key);
    });

    const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

    const conceptsStore = getGlobalConceptsStore();
    const conceptImage = concept.state.images.get(image.key);

    useEffect(() => {
      setThumbnailUrl(null);

      if (!conceptImage) return;

      conceptsStore.imageRegistry
        .renderImage(conceptImage)
        .promise.then((renderedImage) => {
          if (!renderedImage) return null;
          setThumbnailUrl(renderedImage.thumbnailSrc ?? renderedImage.src);
        });
    }, [conceptImage, conceptsStore.imageRegistry]);

    const loading = globalImageLoadingRegistry.has(image.key);

    const { getInputProps, inputRef } = useDropzone({
      accept: {
        'image/jpeg': [],
        'image/gif': [],
        'image/png': [],
        'image/webp': [],
      },
      onDrop: handleUpload,
    });

    return (
      <div
        className={styles.card}
        onMouseEnter={() => {
          globalImageHoverRegistry.add(image.key);
          globalConceptSectionHoverRegistry.add(rootElementId);
        }}
        onMouseLeave={() => {
          globalImageHoverRegistry.delete(image.key);
          globalConceptSectionHoverRegistry.delete(rootElementId);
        }}
      >
        <div
          className={styles.thumbnail}
          style={{
            backgroundImage: thumbnailUrl ? `url(${thumbnailUrl})` : undefined,
            backgroundColor: bgColor,
          }}
        >
          {loading ? (
            <div
              className={styles.placeholderOuter}
              style={{ backgroundColor: placeholderBgColor }}
            >
              <div
                style={{
                  color: placeholderFgColor,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <LoadingDots color="inherit" />
              </div>
            </div>
          ) : (
            placeholderImage &&
            !thumbnailUrl && (
              <div
                className={styles.placeholderOuter}
                style={{ backgroundColor: placeholderBgColor }}
              >
                <div
                  className={styles.placeholderInner}
                  style={{
                    backgroundColor: placeholderFgColor,
                    maskImage: placeholderImage,
                  }}
                />
              </div>
            )
          )}
        </div>
        <div className={styles.content}>
          <div className={styles.title}>
            <TextSmall>Image {index}</TextSmall>
            {conceptImage && (
              <Button
                icon={IconClearSmall}
                variant="tertiary"
                size="small"
                tooltip="Remove image"
                onClick={handleRemove}
                disabled={loading}
              />
            )}
          </div>
          <div className={styles.controls}>
            <Button
              label="Upload"
              icon={IconPng}
              onClick={() => {
                inputRef.current?.click();
              }}
              disabled={loading}
            />
            {/* Reset value to ensure we get notified if user tries to upload the same file again.
             * This is useful when the user uploads a file, then deletes it, and then re-uploads it. */}
            <input {...getInputProps()} value={''} />
          </div>
        </div>
      </div>
    );
  },
);

const useImageUpload = ({
  concept,
  imageKey,
  toastQueue,
}: {
  concept: ConceptAPI;
  imageKey: string;
  toastQueue: ToastQueue;
}) => {
  const onUpload = useStableCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    try {
      await concept.uploadCustomImage(imageKey, acceptedFiles[0]);
    } catch (e) {
      toastQueue.push({
        label: 'Failed to upload image',
      });
    }
  });

  return { onUpload };
};
